import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { IMAGES } from "../utils/GetImages";
import { getTranslation } from "../utils/translation";
import { getAppUrls } from "../utils/config";
import "./AppDescriptionPage.css";

export default function AppDescriptionPage() {
  const { i18n } = useTranslation();
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const RewardPic1 = IMAGES["AppDescriptionPage-RewardPic1.png"];
  const RewardPic2 = IMAGES["AppDescriptionPage-RewardPic2.png"];
  const RewardPic3 = IMAGES["AppDescriptionPage-RewardPic3.png"];
  const DownloadAppImg = IMAGES["AppDescriptionPage-DownloadAppImg.png"];
  const PlayerBenefitImg = IMAGES["AppDescriptionPage-PlayerBenefit.png"];
  const iosAppDownload = IMAGES["AppDescriptionPage-ios_app_download.jpg"];
  const androidAppDownload =
    IMAGES["AppDescriptionPage-android_app_download.svg"];

  // Offers state
  const [offers, setOffers] = useState([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(true);
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0);
  const [showOfferArrows, setShowOfferArrows] = useState(false);

  const steps = [
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step1.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step1.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step2.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step2.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step3.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step3.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step4.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step4.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step5.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step5.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step6.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step6.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step7.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step7.description"
      ),
    },
    {
      title: getTranslation(i18n, "appDescriptionPage.timeline.step8.title"),
      description: getTranslation(
        i18n,
        "appDescriptionPage.timeline.step8.description"
      ),
    },
  ];

  // 3D 卡片傾斜效果
  useEffect(() => {
    // 動畫結束後添加類名，讓 JavaScript 可以控制變換
    const timer = setTimeout(() => {
      const cards = document.querySelectorAll(".feature-card");
      cards.forEach((card) => {
        card.classList.add("animation-complete");
      });
    }, 1500); // 1.5秒後動畫結束

    return () => clearTimeout(timer);
  }, []);

  // 獲取事件數據
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // 獲取今天的日期作為fromDate
        const today = new Date().toISOString().split("T")[0];
        const response = await fetch(
          `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/events?fromDate=${today}`
        );
        const result = await response.json();
        if (result.success && result.data) {
          // 過濾只顯示主要事件且有圖片的事件
          const mainEventsWithImages = result.data;

          console.log("Main events with images:", mainEventsWithImages.length);
          setEvents(mainEventsWithImages);
        } else {
          console.error("API response error:", result);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // 獲取優惠數據
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        // 獲取今天的日期作為fromDate
        const today = new Date().toISOString().split("T")[0];
        const response = await fetch(
          `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/offers?date=${today}`
        );
        const result = await response.json();
        if (result.data) {
          // 過濾只顯示有圖片的優惠
          const offersWithImages = result.data.filter(
            (offer) => offer.imageUrl && offer.imageUrl.trim() !== ""
          );
          console.log("Offers with images:", offersWithImages.length);
          setOffers(offersWithImages);
        } else {
          console.error("Offers API response error:", result);
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
      } finally {
        setIsLoadingOffers(false);
      }
    };

    fetchOffers();
  }, []);

  // 檢查是否需要顯示箭頭
  useEffect(() => {
    setShowArrows(events.length > 3);
  }, [events]);

  // 檢查優惠是否需要顯示箭頭
  useEffect(() => {
    setShowOfferArrows(offers.length > 3);
  }, [offers]);

  // 自動滾動事件
  useEffect(() => {
    if (events.length === 0) return;

    const interval = setInterval(() => {
      setCurrentEventIndex((prevIndex) => {
        // 當到達最後幾個事件時，重新開始
        if (prevIndex >= events.length - 3) {
          return 0;
        }
        return prevIndex + 1;
      });
    }, 2000); // 每2秒切換一次

    return () => clearInterval(interval);
  }, [events]);

  // 自動滾動優惠
  useEffect(() => {
    if (offers.length === 0) return;

    const interval = setInterval(() => {
      setCurrentOfferIndex((prevIndex) => {
        // 當到達最後幾個優惠時，重新開始
        if (prevIndex >= offers.length - 3) {
          return 0;
        }
        return prevIndex + 1;
      });
    }, 2500); // 每2.5秒切換一次

    return () => clearInterval(interval);
  }, [offers]);

  // 手動翻頁函數
  const handlePrevClick = () => {
    setCurrentEventIndex((prevIndex) => {
      if (prevIndex <= 0) {
        return Math.max(0, events.length - 5);
      }
      return prevIndex - 1;
    });
  };

  const handleNextClick = () => {
    setCurrentEventIndex((prevIndex) => {
      if (prevIndex >= events.length - 3) {
        return 0;
      }
      return prevIndex + 1;
    });
  };

  // 優惠手動翻頁函數
  const handleOfferPrevClick = () => {
    setCurrentOfferIndex((prevIndex) => {
      if (prevIndex <= 0) {
        return Math.max(0, offers.length - 5);
      }
      return prevIndex - 1;
    });
  };

  const handleOfferNextClick = () => {
    setCurrentOfferIndex((prevIndex) => {
      if (prevIndex >= offers.length - 3) {
        return 0;
      }
      return prevIndex + 1;
    });
  };

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    if (!card.classList.contains("animation-complete")) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 40;
    const rotateY = (centerX - x) / 40;

    card.style.transition = "transform 0.1s ease-out";
    card.style.transform = `translateY(0) perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;
    if (!card.classList.contains("animation-complete")) return;

    card.style.transition = "transform 0.3s ease-out";
    card.style.transform =
      "translateY(0) perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";
  };

  // 等待翻譯資源載入
  useEffect(() => {
    if (i18n.isInitialized && i18n.store.data) {
      setIsReady(true);
    }
  }, [i18n.isInitialized, i18n.store.data]);

  // 如果還沒準備好，顯示載入狀態
  if (!isReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div>
      <h2 className="app-description-title">
        {getTranslation(i18n, "appDescriptionPage.title")}
      </h2>
      <h2 className="app-description-subtitle">
        {getTranslation(i18n, "appDescriptionPage.subtitle")}
      </h2>
      <section id="features" className="feature-cards">
        <div
          className="feature-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img src={RewardPic1} alt="PTS Icon" className="feature-icon" />
          <h2 className="feature-title">
            {getTranslation(i18n, "appDescriptionPage.feature1.title")}
          </h2>
          <p className="feature-text">
            {getTranslation(i18n, "appDescriptionPage.feature1.description")}
          </p>
        </div>
        <div
          className="feature-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img src={RewardPic2} alt="PTS Icon" className="feature-icon" />
          <h2 className="feature-title">
            {getTranslation(i18n, "appDescriptionPage.feature2.title")}
          </h2>
          <p className="feature-text">
            {getTranslation(i18n, "appDescriptionPage.feature2.description")}
          </p>
        </div>
        <div
          className="feature-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img src={RewardPic3} alt="PTS Icon" className="feature-icon" />
          <h2 className="feature-title">
            {getTranslation(i18n, "appDescriptionPage.feature3.title")}
          </h2>
          <p className="feature-text">
            {getTranslation(i18n, "appDescriptionPage.feature3.description")}
          </p>
        </div>
      </section>

      <section id="download-apps" className="download-apps-section">
        <div className="download-apps-container">
          <div className="download-apps-content">
            <h2 className="download-apps-title">
              {getTranslation(i18n, "appDescriptionPage.downloadApps.title")}
            </h2>
            <p className="download-apps-description">
              {getTranslation(
                i18n,
                "appDescriptionPage.downloadApps.description"
              )}
            </p>
            <div className="download-apps-buttons">
              <img
                src={iosAppDownload}
                alt="Download for iOS"
                className="download-apps-button-image ios-button"
                onClick={() => {
                  const appUrls = getAppUrls();
                  window.open(appUrls.iosUrl, "_blank");
                }}
              />
              <img
                src={androidAppDownload}
                alt="Download for Android"
                className="download-apps-button-image android-button"
                onClick={() => {
                  const appUrls = getAppUrls();
                  window.open(appUrls.androidUrl, "_blank");
                }}
              />
            </div>
          </div>
          <div className="download-apps-image">
            <img
              src={DownloadAppImg}
              alt="Download Apps"
              className="download-apps-img"
            />
          </div>
        </div>
      </section>

      <section id="player-benefits" className="player-benefits-section">
        <div className="player-benefits-container">
          <div className="player-benefits-image">
            <img
              src={PlayerBenefitImg}
              alt="Player Benefits"
              className="player-benefits-img"
            />
          </div>
          <div className="player-benefits-content">
            <h2 className="player-benefits-title">
              {getTranslation(i18n, "appDescriptionPage.playerBenefits.title")}
            </h2>
            <p className="player-benefits-description">
              {getTranslation(
                i18n,
                "appDescriptionPage.playerBenefits.description"
              )}
            </p>
            <ul className="player-benefits-list">
              {getTranslation(
                i18n,
                "appDescriptionPage.playerBenefits.benefits"
              ).map((benefit, index) => (
                <li key={index} className="player-benefits-item">
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="timeline-section">
        <h2 className="timeline-title">
          {getTranslation(i18n, "appDescriptionPage.timeline.startingText")}
        </h2>
        <div className="timeline">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
            >
              <div className="timeline-content">
                <h3>
                  {index + 1}. {step.title}
                </h3>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
        <h2 className="timeline-title">
          {getTranslation(i18n, "appDescriptionPage.timeline.endingText")}
        </h2>
      </section>
    </div>
  );
}
